/* Auth context + hook for Nowcast.
 *
 * Phase 62.9 — Custom Google OAuth (no third-party auth host involved).
 * `signIn()` opens a Google popup via `@react-oauth/google`, gets an
 * authorization code, POSTs it to `/api/auth/google`, and the backend
 * sets the session cookie. No more redirect to auth.emergentagent.com.
 *
 * Wraps the React tree in <AuthProvider>. Exposes:
 *   const { user, isPro, loading, signIn, signOut, refresh } = useAuth();
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";
import { getFavourites, pushFavouritesToBackend } from "@/lib/favourites";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext({
  user: null,
  isPro: false,
  loading: true,
  refresh: async () => {},
  signIn: () => {},
  signOut: async () => {},
  signInError: null,
  signingIn: false,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signInError, setSignInError] = useState(null);
  const [signingIn, setSigningIn] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(r.data?.user || null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Mirror localStorage favourites to the backend whenever a signed-in
  // user's session is (re-)established. The sweeper iterates this list
  // for storm-watch alerts, so we want it to match the on-device state
  // as soon as auth resolves.
  useEffect(() => {
    if (!user) return;
    pushFavouritesToBackend(getFavourites());
  }, [user?.user_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      setSignInError(null);
      try {
        const r = await axios.post(
          `${API}/auth/google`,
          { code, redirect_uri: "postmessage" },
          { withCredentials: true },
        );
        setUser(r.data?.user || null);
      } catch (e) {
        setSignInError(
          e?.response?.data?.detail ||
            "Sign-in could not be completed. Please try again.",
        );
        setUser(null);
      } finally {
        setSigningIn(false);
      }
    },
    onError: (err) => {
      setSigningIn(false);
      // Treat user-cancelled popups as silent — anything else surfaces.
      if (err?.error === "popup_closed_by_user" || err?.type === "popup_closed") {
        setSignInError(null);
      } else {
        setSignInError("Google sign-in was blocked. Try again or check your popup settings.");
      }
    },
  });

  const signIn = useCallback(() => {
    setSignInError(null);
    setSigningIn(true);
    googleLogin();
  }, [googleLogin]);

  const signOut = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* network blip — local state cleared anyway */
    }
    setUser(null);
  }, []);

  const value = {
    user,
    isPro: !!user?.is_pro,
    loading,
    refresh,
    signIn,
    signOut,
    signInError,
    signingIn,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
